
import React, { useRef, useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Child } from '@/types/child';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Camera, User } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface EditProfileDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  child: Child | null;
  onChildChange: (child: Child) => void;
  onSave: () => void;
  onPhotoChange?: (file: File) => void;
}

const EditProfileDialog: React.FC<EditProfileDialogProps> = ({ 
  isOpen, 
  onOpenChange, 
  child, 
  onChildChange, 
  onSave,
  onPhotoChange
}) => {
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();
  
  useEffect(() => {
    // Reset photo preview when dialog opens with new child data
    if (isOpen && child?.photoUrl) {
      setPhotoPreview(child.photoUrl);
    } else if (!isOpen) {
      setPhotoPreview(null);
    }
  }, [isOpen, child]);

  if (!child) return null;

  const handlePhotoClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Check file type
    if (!file.type.startsWith('image/')) {
      toast({
        title: "Invalid file type",
        description: "Please select an image file (JPEG, PNG, etc.)",
        variant: "destructive"
      });
      return;
    }

    // Check file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast({
        title: "File too large",
        description: "Please select an image smaller than 5MB",
        variant: "destructive"
      });
      return;
    }

    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setPhotoPreview(reader.result as string);
    };
    reader.readAsDataURL(file);

    // Pass the file to parent component
    if (onPhotoChange) {
      onPhotoChange(file);
    }
  };
  
  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit Profile</DialogTitle>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="flex justify-center mb-2">
            <div 
              onClick={handlePhotoClick}
              className="relative cursor-pointer"
            >
              <Avatar className="h-24 w-24">
                {photoPreview ? (
                  <AvatarImage src={photoPreview} alt={child.name} />
                ) : (
                  <AvatarFallback className="bg-gray-200">
                    <User className="h-12 w-12 text-gray-400" />
                  </AvatarFallback>
                )}
              </Avatar>
              <div className="absolute bottom-0 right-0 bg-primary rounded-full p-1">
                <Camera className="h-4 w-4 text-white" />
              </div>
            </div>
            <input 
              type="file"
              ref={fileInputRef}
              className="hidden"
              accept="image/*"
              onChange={handleFileChange}
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="name">Name</Label>
            <Input 
              id="name" 
              value={child.name}
              onChange={(e) => onChildChange({...child, name: e.target.value})}
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="birthdate">Birthdate</Label>
            <Input 
              id="birthdate" 
              type="date" 
              value={child.birthdate}
              onChange={(e) => onChildChange({...child, birthdate: e.target.value})}
            />
          </div>
          <div className="flex justify-end space-x-2 mt-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button onClick={onSave}>Save Changes</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default EditProfileDialog;
